import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MAX_SELECTED_YEARS, MIN_SELECTED_YEARS } from '../../models/controls.model';

interface YearPillVM {
    /** Opaque key = BE `yearLabel` (full ISO date `YYYY-MM-DD`). */
    key: string;
    /** Human-friendly label shown on the pill. */
    display: string;
    isSelected: boolean;
    isDisabled: boolean;
}

@Component({
    selector: 'year-pills',
    templateUrl: './year-pills.component.html',
    styleUrls: ['./year-pills.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YearPillsComponent {
    /** All available year labels (ISO dates), newest first. */
    @Input() public yearLabels: string[] = [];
    /** Currently selected subset. */
    @Input() public selectedYears: string[] = [];

    @Output() public toggle = new EventEmitter<string>();

    public readonly minSelected = MIN_SELECTED_YEARS;
    public readonly maxSelected = MAX_SELECTED_YEARS;

    public get pills(): YearPillVM[] {
        const selectedSet = new Set(this.selectedYears);
        const selectedCount = selectedSet.size;
        return this.yearLabels.map((label) => {
            const isSelected = selectedSet.has(label);
            const isDisabled =
                (isSelected && selectedCount === this.minSelected) ||
                (!isSelected && selectedCount === this.maxSelected);
            return {
                key: label,
                display: this.formatDisplay(label),
                isSelected,
                isDisabled,
            };
        });
    }

    public trackByKey(_idx: number, pill: YearPillVM): string {
        return pill.key;
    }

    public onClick(pill: YearPillVM): void {
        if (pill.isDisabled) return;
        this.toggle.emit(pill.key);
    }

    /**
     * `YYYY-12-31` → just the year (year-end is the common case, keep it concise).
     * Any other date → `DD.MM.YYYY`. Non-ISO inputs are returned as-is.
     */
    private formatDisplay(label: string): string {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
        if (m == null) return label;
        const [, y, mo, d] = m;
        if (mo === '12' && d === '31') return y;
        return `${d}.${mo}.${y}`;
    }
}
